/**
 * The terms of service and privacy policy, at /terms and /privacy, readable
 * without signing in.
 *
 * ## One source
 *
 * The text lives in `docs/legal/*.md`, where it was drafted and reviewed, and
 * is imported here as raw text at build time. Editing the document changes
 * the page; there is no second copy to forget.
 *
 * ## A small renderer, not a library
 *
 * The documents use headings, paragraphs, bullet lists, simple tables, bold
 * and email addresses, nothing more. Twenty lines of parsing cover that;
 * a markdown package would be a dependency for two pages (CLAUDE.md on
 * dependencies). If the documents ever need more, that is the time to add one.
 */
import { Fragment, type ReactNode } from 'react';
import privacySource from '../../../../docs/legal/PRIVACY-POLICY.md?raw';
import termsSource from '../../../../docs/legal/TERMS-OF-SERVICE.md?raw';
import { c, sans } from '../theme.js';

export type LegalDoc = 'terms' | 'privacy';

const SOURCE: Record<LegalDoc, string> = { terms: termsSource, privacy: privacySource };

type Block =
  | { kind: 'h1' | 'h2' | 'h3' | 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'table'; rows: string[][] };

/** Splits the markdown into blocks. Exported for the test. */
export function parseLegal(source: string): Block[] {
  const lines = source.replace(/<!--[\s\S]*?-->/g, '').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) blocks.push({ kind: 'p', text: para.join(' ') });
    para = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const last = blocks[blocks.length - 1];
    if (line.trim() === '' || line.trim() === '---') {
      flush();
      continue;
    }
    const heading = /^(#{1,3}) (.*)$/.exec(line);
    if (heading) {
      flush();
      blocks.push({
        kind: (['h1', 'h2', 'h3'] as const)[heading[1]!.length - 1]!,
        text: heading[2]!,
      });
    } else if (line.startsWith('- ')) {
      flush();
      if (last?.kind === 'ul') last.items.push(line.slice(2));
      else blocks.push({ kind: 'ul', items: [line.slice(2)] });
    } else if (/^\s+\S/.test(line) && last?.kind === 'ul' && para.length === 0) {
      // A wrapped bullet: indented continuation of the last item.
      last.items[last.items.length - 1] += ` ${line.trim()}`;
    } else if (line.startsWith('|')) {
      flush();
      const cells = line
        .slice(1, -1)
        .split('|')
        .map((cell) => cell.trim());
      if (cells.every((cell) => /^-+$/.test(cell))) continue; // the |---| row
      if (last?.kind === 'table') last.rows.push(cells);
      else blocks.push({ kind: 'table', rows: [cells] });
    } else {
      para.push(line.trim());
    }
  }
  flush();
  return blocks;
}

/** **bold** and email addresses; everything else is plain text. */
function inline(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*|[\w.+-]+@[\w-]+\.[\w.]+)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(part)) {
      return (
        <a key={i} href={`mailto:${part}`} style={{ color: c.green }}>
          {part}
        </a>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

export function LegalPage({ doc, onHome }: { doc: LegalDoc; onHome: () => void }) {
  const blocks = parseLegal(SOURCE[doc]);
  const body: React.CSSProperties = {
    fontSize: 15.5,
    lineHeight: 1.7,
    color: c.ink,
    margin: '0 0 14px',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#F5F5F2',
        fontFamily: sans,
        padding: '32px 16px 72px',
      }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <button
          type="button"
          onClick={onHome}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            fontFamily: sans,
            fontSize: 14,
            color: c.green,
            cursor: 'pointer',
            marginBottom: 18,
          }}
        >
          Back to The Good Stock
        </button>
        <article
          style={{ background: '#fff', borderRadius: 18, padding: 'clamp(22px, 5vw, 44px)' }}
        >
          {blocks.map((b, i) => {
            if (b.kind === 'h1') {
              return (
                <h1
                  key={i}
                  style={{
                    fontSize: 36,
                    fontWeight: 300,
                    letterSpacing: '-0.03em',
                    margin: '0 0 8px',
                  }}
                >
                  {b.text}
                </h1>
              );
            }
            if (b.kind === 'h2') {
              return (
                <h2
                  key={i}
                  style={{
                    fontSize: 21,
                    fontWeight: 400,
                    letterSpacing: '-0.02em',
                    margin: '30px 0 10px',
                  }}
                >
                  {b.text}
                </h2>
              );
            }
            if (b.kind === 'h3') {
              return (
                <h3 key={i} style={{ fontSize: 16.5, fontWeight: 500, margin: '22px 0 8px' }}>
                  {b.text}
                </h3>
              );
            }
            if (b.kind === 'ul') {
              return (
                <ul key={i} style={{ ...body, paddingLeft: 22 }}>
                  {b.items.map((item, j) => (
                    <li key={j} style={{ marginBottom: 6 }}>
                      {inline(item)}
                    </li>
                  ))}
                </ul>
              );
            }
            if (b.kind === 'table') {
              const [head, ...rows] = b.rows;
              return (
                <div key={i} style={{ overflowX: 'auto', margin: '0 0 16px' }}>
                  <table
                    style={{
                      borderCollapse: 'collapse',
                      width: '100%',
                      fontSize: 14.5,
                      lineHeight: 1.55,
                    }}
                  >
                    <thead>
                      <tr>
                        {head!.map((cell, j) => (
                          <th
                            key={j}
                            style={{
                              textAlign: 'left',
                              fontWeight: 500,
                              padding: '8px 10px',
                              borderBottom: `1px solid ${c.border}`,
                            }}
                          >
                            {inline(cell)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, j) => (
                        <tr key={j}>
                          {row.map((cell, k) => (
                            <td
                              key={k}
                              style={{
                                padding: '8px 10px',
                                borderBottom: `1px solid ${c.border}`,
                                verticalAlign: 'top',
                              }}
                            >
                              {inline(cell)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }
            return (
              <p key={i} style={body}>
                {inline(b.text)}
              </p>
            );
          })}
        </article>
      </div>
    </div>
  );
}
